<template>
	<auth-layout>
		<div class="col-lg-7 align-self-center">
			<h1>{{ $t('login.welcome') }}</h1>

			<b-form @submit.prevent="login" novalidate>
				<text-input
					name="email"
					:label="$t('login.labels.email')"
					autocomplete="email"
					:autofocus="true"
					:error="emailError"
					v-model="email"
				/>

				<text-input
					name="password"
					:label="$t('login.labels.password')"
					type="password"
					autocomplete="current-password"
					:error="passwordError"
					v-model="password"
				/>

				<div class="form-group row">
					<!-- <div class="col">
						<div class="form-group">
							<checkbox-input
									name="remember"
									:label="$t('login.labels.remember')"
									v-model="remember"
							/>
						</div>
					</div> -->

					<div class="col">
						<div class="form-group text-right">
							<router-link :to="{ name: 'forgot-password' }">{{ $t('login.forgot_password') }}</router-link>
						</div>
					</div>
				</div>

				<div class="form-group">
					<b-button
						type="submit"
						variant="primary"
						block
						:disabled="processing"
					>
						<b-spinner small v-if="processing" />
						{{ $t('login.submit_btn') }}
					</b-button>
				</div>
			</b-form>
		</div>
	</auth-layout>
</template>

<script>
import AuthLayout from '../layouts/AuthLayout';
import TextInput from '../../components/forms/TextInput';
// import CheckboxInput from '../../components/forms/CheckboxInput'
import { required, email as vEmail } from 'vuelidate/lib/validators';
import nprogress from 'nprogress';
import validate from '../../util/validate';
import ErrorModal from '../../mixins/ErrorModal';

export default {
	inject: ['tokenRepository', 'userRepository'],
	components: {
		AuthLayout,
		TextInput
		// CheckboxInput
	},

	mixins: [ ErrorModal ],

	data () {
		return {
			email: process.env.VUE_APP_DEFAULT_EMAIL,
			password: process.env.VUE_APP_DEFAULT_PASSWORD,
			remember: false,
			processing: false,
			submitted: false,
			serverError: null
		};
	},

	methods: {
		/**
		 * Loads the token and user from repositories and dispatches that data to a store action. Once
		 * complete, the user is routed to the dashboard.
		 *
		 * On an error, the process is halted and an error is shown to the user.
		 *
		 * @returns {void}
		 */
		async login () {
			this.processing = true;
			this.submitted = true;

			if (this.$v.$invalid) {
				this.processing = false;
			}
			else {
				nprogress.start();

				try {
					await this.$store.dispatch(
						'auth/login',
						await this.tokenRepository.get(this.email, this.password)
					);
				}
				catch (error) {
					this.serverError = 'There was an error in logging you in. \n Ensure your email and password are correct and try again';
					this.processing = false;
					nprogress.done();
					throw error;
				}

				try {
					if (this.user) {
						await this.$store.dispatch('sidebar/refresh', {});
					}
				}
				catch (error) {
					//
				}

				this.$router.replace({ name: 'dashboard' }, () => {});
			}
		}
	},

	validations: {
		email: {
			required,
			vEmail
		},
		password: {
			required
		}
	},

	computed: {
		/**
		 * Returns a string representing the email error, if any. If no error exists,
		 * an empty string is returned.
		 *
		 * @returns {string}
		 */
		emailError () {
			if (this.submitted) {
				if (!this.$v.email.required) {
					return this.$t('validation.required');
				}
				if (!this.$v.email.vEmail) {
					return this.$t('validation.email');
				}
				if (validate.getServerError(this.serverError, 'email') !== '') {
					return validate.getServerError(this.serverError, 'email');
				}
			}

			return '';
		},

		/**
		 * Returns a string representing the password error, if any. If no error exists,
		 * an empty string is returned.
		 *
		 * @returns {string}
		 */
		passwordError () {
			if (this.submitted) {
				if (!this.$v.password.required) {
					return this.$t('validation.required');
				}
				if (validate.getServerError(this.serverError, 'password') !== '') {
					return validate.getServerError(this.serverError, 'password');
				}
			}

			return '';
		},

		genericServerError () {
			return validate.getGenericError(this.serverError);
		},

		user () {
			return this.$store.getters['auth/currentUser'];
		}
	},

	watch: {
		genericServerError () {
			if (this.genericServerError !== null) {
				this.showErrorModal(this.genericServerError, 'Login Error')
					.then(() => {
						this.serverError = null;
					});
			}
		}
	}
};
</script>
